/**
 * PrivacyPrefsContext — one shared source of truth for the member's
 * Browser Disguise + Discreet Filename choices.
 *
 * Was a plain hook before iter209 test found that PrivacyPresetsCard
 * and BrowserDisguiseApplier were seeing different copies of state —
 * clicking a radio in Settings didn't update the tab title live.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import { getToken } from '../utils/tokenManager';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT = {
  browser_disguise: 'myinvestfolio',
  browser_disguise_custom: '',
  filename_prefix: 'myinvestfolio',
  filename_prefix_custom: '',
};

const PrivacyPrefsContext = createContext({
  prefs: DEFAULT,
  loading: true,
  saving: false,
  updatePref: async () => {},
});

export function PrivacyPrefsProvider({ children }) {
  const [prefs, setPrefs] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPrefs = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/users/me/privacy-prefs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setPrefs(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const updatePref = useCallback(async (key, value) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/users/me/privacy-prefs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [key]: value }),
      });
      if (res.ok) setPrefs(await res.json());
    } finally {
      setSaving(false);
    }
  }, []);

  return (
    <PrivacyPrefsContext.Provider value={{ prefs, loading, saving, updatePref }}>
      {children}
    </PrivacyPrefsContext.Provider>
  );
}

export function usePrivacyPrefs() {
  return useContext(PrivacyPrefsContext);
}
